import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";

const STATS = [
  { value: 15, prefix: "", suffix: "+", label: "Carriers" },
  { value: 230, prefix: "$", suffix: "K+", label: "Saved for our clients" },
  { value: 15, prefix: "", suffix: "", label: "Years of experience" },
];

function Counter({ to, prefix = "", suffix = "", inView }) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (latest) => setVal(Math.round(latest)),
    });
    return () => controls.stop();
    // setVal is stable; `animate` is a module-level import from framer-motion.
    // Re-run only when the counter enters view or the target value changes.
  }, [inView, to]);

  return (
    <span className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-none tracking-tight tabular-nums">
      {prefix}
      {val}
      {suffix}
    </span>
  );
}

export default function StatsRibbon() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-24 bg-[#011233] overflow-hidden"
      data-testid="stats-ribbon"
    >
      {/* Subtle silver glow on edges */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#B5BAC2]/10 blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-[#B5BAC2]/10 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 sm:mb-14"
        >
          <p className="text-[12px] uppercase tracking-[0.36em] text-[#B5BAC2] font-semibold">
            By the numbers
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-6 text-center">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
                delay: 0.1 + i * 0.12,
              }}
              className="flex flex-col items-center"
              data-testid={`stat-${i}`}
            >
              <Counter
                to={s.value}
                prefix={s.prefix}
                suffix={s.suffix}
                inView={inView}
              />
              <p className="mt-4 text-[12px] uppercase tracking-[0.28em] text-[#B5BAC2] font-semibold">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
